import { IMPACT_SCORES, NOT_PROVIDED_SCORE } from './constants.js';
import { parseCountryList, classifyTocAlignment } from './parsers.js';
import { combineRawPartnerTexts, isCGIARCenter, parseContributingCenters } from './partnershipParsers.js';
import { isGlobalSouthCountry } from '../countryMapping.js';

// Calculate aggregated statistics
// Optional filters param allows country-level filtering (e.g. Global South)
export function calculateStats(results, filters) {
  const stats = {
    total: results.length,
    byLevel: {},
    byType: {},
    byYear: {},
    byImpactArea: {
      gender: { '-1': 0, 0: 0, 1: 0, 2: 0 },
      climate: { '-1': 0, 0: 0, 1: 0, 2: 0 },
      nutrition: { '-1': 0, 0: 0, 1: 0, 2: 0 },
      biodiversity: { '-1': 0, 0: 0, 1: 0, 2: 0 },
      poverty: { '-1': 0, 0: 0, 1: 0, 2: 0 },
    },
    // Funding type — now based on the explicit fundingType field from the source file
    byFundingType: { pooled: 0, nonpooled: 0 },
    byTocPathway: { 'aligned-planned': 0, 'aligned-unplanned': 0, 'linked': 0, 'other': 0 },
    // Per-type funding split for detailed charts
    byTypeAndFunding: {},
    countries: new Set(),
    resultsWithGeo: 0,
    countryResultCounts: {},
    regionResultCounts: {},
    byInnovationUseLevel: {},
    byReadinessLevel: {},
    regions: new Set(),
    partners: new Set(),
    externalPartners: new Set(),
  };

  results.forEach(result => {
    // By level
    if (result.level) {
      stats.byLevel[result.level] = (stats.byLevel[result.level] || 0) + 1;
    }

    // By type
    if (result.type) {
      stats.byType[result.type] = (stats.byType[result.type] || 0) + 1;
    }

    // By year
    if (result.year) {
      stats.byYear[result.year] = (stats.byYear[result.year] || 0) + 1;
    }

    // By impact area
    const genderScore = result.genderLevel ? (IMPACT_SCORES[result.genderLevel] ?? 0) : NOT_PROVIDED_SCORE;
    const climateScore = result.climateLevel ? (IMPACT_SCORES[result.climateLevel] ?? 0) : NOT_PROVIDED_SCORE;
    const nutritionScore = result.nutritionLevel ? (IMPACT_SCORES[result.nutritionLevel] ?? 0) : NOT_PROVIDED_SCORE;
    const biodiversityScore = result.biodiversityLevel ? (IMPACT_SCORES[result.biodiversityLevel] ?? 0) : NOT_PROVIDED_SCORE;
    const povertyScore = result.povertyLevel ? (IMPACT_SCORES[result.povertyLevel] ?? 0) : NOT_PROVIDED_SCORE;

    stats.byImpactArea.gender[genderScore]++;
    stats.byImpactArea.climate[climateScore]++;
    stats.byImpactArea.nutrition[nutritionScore]++;
    stats.byImpactArea.biodiversity[biodiversityScore]++;
    stats.byImpactArea.poverty[povertyScore]++;

    // By funding type — uses the EXPLICIT fundingType field (not derived from nonPooledProjects)
    if (result.fundingType === 'W3Bilateral') {
      stats.byFundingType.nonpooled++;
    } else {
      stats.byFundingType.pooled++;
    }

    // By type AND funding (for split charts)
    if (result.type) {
      if (!stats.byTypeAndFunding[result.type]) {
        stats.byTypeAndFunding[result.type] = { W1W2: 0, W3Bilateral: 0, total: 0 };
      }
      stats.byTypeAndFunding[result.type][result.fundingType === 'W3Bilateral' ? 'W3Bilateral' : 'W1W2']++;
      stats.byTypeAndFunding[result.type].total++;
    }

    // By TOC pathway
    if (result.tocMappingType) {
      stats.byTocPathway[result.tocMappingType] = (stats.byTocPathway[result.tocMappingType] || 0) + 1;
    }

    // Countries (with counts for map) — use pre-parsed array if available
    const countryList = result.countriesParsed || (result.countries ? parseCountryList(result.countries) : []);
    const globalSouthOnly = filters?.geographicFocus === 'globalSouth';
    if (countryList.length > 0) stats.resultsWithGeo++;
    countryList.forEach(country => {
      if (globalSouthOnly && !isGlobalSouthCountry(country)) return;
      stats.countries.add(country);
      stats.countryResultCounts[country] = (stats.countryResultCounts[country] || 0) + 1;
    });

    // Regions (with counts) — use pre-parsed array if available
    const regionList = result.regionsParsed || (result.regions ? parseCountryList(result.regions) : []);
    regionList.forEach(region => {
      stats.regions.add(region);
      stats.regionResultCounts[region] = (stats.regionResultCounts[region] || 0) + 1;
    });

    // Innovation Use Level (1-9 scale)
    if (result.innovationUseLevel != null) {
      const lvl = parseInt(result.innovationUseLevel, 10);
      if (lvl >= 1 && lvl <= 9) {
        stats.byInnovationUseLevel[lvl] = (stats.byInnovationUseLevel[lvl] || 0) + 1;
      }
    }

    // Readiness Level (1-9 scale)
    if (result.readinessLevel != null) {
      const lvl = parseInt(result.readinessLevel, 10);
      if (lvl >= 1 && lvl <= 9) {
        stats.byReadinessLevel[lvl] = (stats.byReadinessLevel[lvl] || 0) + 1;
      }
    }

    // Partners — use pre-parsed array if available, otherwise parse from raw text
    const allPartnersParsed = [
      ...(result.partnersParsed || []),
      ...(result.partnersKPParsed || []),
    ];
    if (allPartnersParsed.length > 0) {
      allPartnersParsed.forEach(p => {
        if (p.acronym) {
          const dedupKey = p.fullName || p.acronym;
          stats.partners.add(dedupKey);
          if (!isCGIARCenter(p.acronym)) {
            stats.externalPartners.add(dedupKey);
          }
        }
      });
    } else {
      // Fallback: parse from raw text (legacy/manual upload path, merge both columns)
      const partnerText = combineRawPartnerTexts(result.partners, result.partnersKP);
      if (partnerText) {
        const entries = partnerText.split('•').map(s => s.trim()).filter(Boolean);
        entries.forEach(entry => {
          const semiIdx = entry.indexOf(';');
          const namePart = semiIdx > -1 ? entry.substring(0, semiIdx).trim() : entry.trim();
          const dashIdx = namePart.indexOf(' - ');
          const acronym = dashIdx > -1 ? namePart.substring(0, dashIdx).trim() : namePart;
          const fullName = dashIdx > -1 ? namePart.substring(dashIdx + 3).trim() : namePart;
          const dedupKey = fullName || acronym;
          if (acronym) {
            stats.partners.add(dedupKey);
            if (!isCGIARCenter(acronym)) {
              stats.externalPartners.add(dedupKey);
            }
          }
        });
      }
    }

  });

  // Convert Sets to arrays
  stats.countries = [...stats.countries].sort();
  stats.regions = [...stats.regions].sort();
  stats.partners = [...stats.partners].sort();
  stats.externalPartners = [...stats.externalPartners].sort();

  // Contributing Centers — use parseContributingCenters for consistency with Section 3 chart
  stats.contributingCenters = [...parseContributingCenters(results).keys()].sort();

  return stats;
}
